import {
  type KdsColumn,
  kdsColumnFor,
  type Order,
  type OrderStatus,
  type RealtimeEvent,
} from '@momonos/shared'

/**
 * The kitchen board's state, kept as a pure reducer.
 *
 * Separated from React so the rules that matter — a live event never
 * contradicts a newer snapshot, an order leaves the board when it should — are
 * testable without rendering anything.
 */

export interface BoardState {
  /** Keyed by order id; the board renders these grouped into columns. */
  orders: Record<string, Order>
  /** Orders that arrived since the last acknowledgement, for the flash + chime. */
  unseen: string[]
  lastEventAt: number | null
}

export const emptyBoard: BoardState = { orders: {}, unseen: [], lastEventAt: null }

export type BoardAction =
  /** FR-RT-4 — authoritative state, replacing whatever was on screen. */
  | { type: 'snapshot'; orders: Order[] }
  | { type: 'event'; event: RealtimeEvent; at?: number }
  /** The screen has shown the flash and played the chime. */
  | { type: 'acknowledge'; orderId: string }
  /** Optimistic move while the PATCH is in flight. */
  | { type: 'optimistic-status'; orderId: string; status: OrderStatus }

export function boardReducer(state: BoardState, action: BoardAction): BoardState {
  switch (action.type) {
    case 'snapshot': {
      const orders: Record<string, Order> = {}
      for (const order of action.orders) orders[order.id] = order

      // Anything the snapshot no longer contains has left the kitchen, so its
      // pending flash is dropped too.
      return {
        orders,
        unseen: state.unseen.filter((id) => id in orders),
        lastEventAt: state.lastEventAt,
      }
    }

    case 'event': {
      const next = applyEvent(state, action.event)
      return { ...next, lastEventAt: action.at ?? Date.now() }
    }

    case 'acknowledge':
      return { ...state, unseen: state.unseen.filter((id) => id !== action.orderId) }

    case 'optimistic-status': {
      const order = state.orders[action.orderId]
      if (!order) return state

      return {
        ...state,
        orders: { ...state.orders, [action.orderId]: { ...order, status: action.status } },
      }
    }

    default:
      return state
  }
}

function applyEvent(state: BoardState, event: RealtimeEvent): BoardState {
  switch (event.type) {
    case 'ORDER_CREATED':
    case 'ORDER_UPDATED':
    case 'PAYMENT_COMPLETED': {
      const summary = 'order' in event.payload ? event.payload.order : undefined
      if (!summary) return state

      const existing = state.orders[summary.id]

      // A summary is thinner than a full order: merge onto what we already have
      // rather than replacing it, so item lines are not lost mid-service.
      const merged: Order = existing
        ? { ...existing, status: summary.status, isPaid: summary.isPaid }
        : ({
            ...summary,
            branchId: event.branchId,
            tableId: null,
            customerName: null,
            customerPhone: null,
            items: [],
            subtotal: 0,
            discount: 0,
            vat: 0,
            couponCode: null,
            note: null,
            estimatedWaitSeconds: summary.prepSeconds ?? null,
            prepStartedAt: null,
            createdAt: summary.placedAt,
            updatedAt: summary.placedAt,
          } as unknown as Order)

      // Only an order the kitchen actually has to cook belongs on the board.
      if (!kdsColumnFor(merged.status)) {
        return removeOrder(state, merged.id)
      }

      const isNew = !existing && merged.status === 'placed'
      return {
        ...state,
        orders: { ...state.orders, [merged.id]: merged },
        unseen: isNew ? [...state.unseen, merged.id] : state.unseen,
      }
    }

    case 'ORDER_ACCEPTED':
    case 'ORDER_PREPARING':
    case 'ORDER_READY':
    case 'ORDER_DELIVERED': {
      const { orderId, to } = event.payload
      const existing = state.orders[orderId]
      if (!existing) return state

      if (!kdsColumnFor(to)) return removeOrder(state, orderId)

      return {
        ...state,
        orders: { ...state.orders, [orderId]: { ...existing, status: to } },
      }
    }

    default:
      return state
  }
}

function removeOrder(state: BoardState, orderId: string): BoardState {
  const { [orderId]: _removed, ...rest } = state.orders
  return { ...state, orders: rest, unseen: state.unseen.filter((id) => id !== orderId) }
}

/** Groups the board into its four columns, oldest first within each. */
export function columnsOf(state: BoardState): Record<KdsColumn, Order[]> {
  const columns: Record<KdsColumn, Order[]> = {
    new: [],
    preparing: [],
    ready: [],
    delivered: [],
  }

  for (const order of Object.values(state.orders)) {
    const column = kdsColumnFor(order.status)
    if (column) columns[column].push(order)
  }

  for (const list of Object.values(columns)) {
    // Priority first, then oldest — the order a kitchen actually works in.
    list.sort((a, b) => {
      if (a.isPriority !== b.isPriority) return a.isPriority ? -1 : 1
      return a.placedAt - b.placedAt
    })
  }

  return columns
}

/**
 * FR-KDS-3 — how long is left on an order's cooking countdown.
 * Negative means overdue, which the card renders red and alarms on.
 */
export function remainingSeconds(order: Order, now: number): number {
  const startedAt = order.prepStartedAt ?? order.placedAt
  const budget = order.estimatedWaitSeconds ?? 0
  return Math.round((startedAt + budget * 1000 - now) / 1000)
}

export function isOverdue(order: Order, now: number): boolean {
  return remainingSeconds(order, now) < 0
}
